import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequest, isIntegratedApp } from "utils/httpClient";
import { RootState } from "../store";
import { getBankAccountsPath, integratedGetPaynoteFundingSourcesPath } from "utils/backend-path-builders";

import { BankAccount, BankAccountsResponse, BankTransferProvider } from "interfaces/commerce/bank-account.ts";
import { ACH_MINIMUM_DEPOSIT, PLAID_MINIMUM_DEPOSIT } from "utils/constants.ts";

export interface BankAccountsState {
  accounts: BankAccount[];
  depositProvider: BankTransferProvider;
  withdrawalProvider: BankTransferProvider;
  fetchStatusState: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  isPolling: boolean;
}

const initialState: BankAccountsState = {
  accounts: [],
  depositProvider: "PAYNOTE",
  withdrawalProvider: "PAYNOTE",
  fetchStatusState: "idle",
  error: null,
  isPolling: false,
};

interface PaynoteFundingSource {
  id: string;
  bankName: string;
  accountType: string;
  last4: string;
  isDefault: boolean;
  status: string;
  created_at: string;
}

// Integrated apps only reach the PayNote funding-source list.
const fetchIntegratedBankAccounts = async (): Promise<BankAccountsResponse> => {
  const sources: PaynoteFundingSource[] = (await getRequest(integratedGetPaynoteFundingSourcesPath())) || [];
  return {
    bank_deposit_provider: "PAYNOTE",
    bank_withdrawal_provider: "PAYNOTE",
    accounts: sources.map((s) => ({
      id: s.id,
      provider: "PAYNOTE",
      bankName: s.bankName,
      last4: s.last4,
      type: s.accountType,
      isDefault: s.isDefault,
      status: s.status,
      created_at: s.created_at,
    })),
  };
};

export const fetchBankAccounts = createAsyncThunk<
  BankAccountsResponse,
  void,
  { rejectValue: string }
>("bankAccounts/fetch", async (_, { rejectWithValue }) => {
  try {
    if (isIntegratedApp()) {
      return await fetchIntegratedBankAccounts();
    }
    return await getRequest(getBankAccountsPath());
  } catch (err) {
    return rejectWithValue("Failed to fetch bank accounts");
  }
});

const slice = createSlice({
  name: "bankAccounts",
  initialState,
  reducers: {
    clearBankAccountsState: () => initialState,
    setPolling: (state, action) => {
      state.isPolling = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBankAccounts.pending, (state) => {
      state.fetchStatusState = "loading";
      state.error = null;
    });
    builder.addCase(fetchBankAccounts.fulfilled, (state, action) => {
      state.fetchStatusState = "succeeded";
      state.accounts = action.payload.accounts || [];
      state.depositProvider = action.payload.bank_deposit_provider;
      state.withdrawalProvider = action.payload.bank_withdrawal_provider;
      state.error = null;
    });
    builder.addCase(fetchBankAccounts.rejected, (state, action) => {
      state.fetchStatusState = "failed";
      state.error = action.payload ?? "Something went wrong.";
    });
  },
});

export const getBankAccountsState: (state: RootState) => BankAccountsState = (state: RootState) => state.bankAccounts;
export const getBankDepositMinimum = (state: RootState) =>
  state.bankAccounts.depositProvider === "PLAID" ? PLAID_MINIMUM_DEPOSIT : ACH_MINIMUM_DEPOSIT;

export const { clearBankAccountsState, setPolling } = slice.actions;

export default slice.reducer;
